/* Borrador local del editor Seedance: sobrevive a una recarga o a un remontaje
 * y NUNCA gasta ni inicia nada por sí solo.
 *
 * POR QUÉ EXISTE. El estado del editor (prompt, referencias, ajustes) vive solo
 * en `useState`. Una recarga —la de un deploy, la de un cambio de proyecto, un
 * F5 o un cierre accidental— lo tira entero. Este módulo es la mitad PURA de la
 * solución: qué se guarda, qué jamás se guarda, cómo se valida al volver y cómo
 * se decide si algo está "sin enviar". Sin React y sin dependencias a propósito:
 * el gate corre los `*.test.mjs` con node antes del `npm ci`.
 *
 * QUÉ SE GUARDA. Solo lo que identifica el trabajo del operador y se puede
 * volver a resolver en el servidor: prompt, campos de plantilla, localizadores
 * ORDENADOS de fuente/referencias (storage_path, asset_id, URL propia), ajustes
 * que cambian el contrato (modelo, resolución, brazo del compilador, ratio,
 * duración, seed, audio/draft…) y los ids de los jobs YA aceptados.
 *
 * QUÉ NO SE GUARDA, NUNCA:
 *   · `File`/`Blob`/`blob:` — no son serializables y fingirlos sería peor que
 *     perderlos. Se guardan solo nombre/tamaño para AVISAR de que hay que
 *     volver a elegir el fichero (`localFiles`).
 *   · previews derivados (thumbnail_url, public_url, URLs firmadas) y cualquier
 *     URL con credenciales: caducan o son secretos.
 *   · consentimientos y estados de operación: propuesta/presentación del
 *     asistente, aprobaciones, "generando", "subiendo", "armado", preparación
 *     de ventana de edición/unión. Restaurar uno de ellos podría gastar dinero
 *     sin que el operador haya vuelto a pulsar nada.
 *
 * ÁMBITO. La clave lleva usuario + proyecto activo + pestaña + espacio de
 * trabajo, y el registro repite esos cuatro datos: se comprueban al leer, así
 * que una clave pisada a mano o heredada no restaura nada ajeno.
 */

export const DRAFT_VERSION = 1;
export const DRAFT_WORKSPACE = "seedance-editor";
const PREFIX = "orca:editor-draft:v1";
const TAB_ID_KEY = "orca:tab-id";
// Techo de tamaño de UN borrador. sessionStorage ronda los 5 MB por origen; un
// borrador que no cabe se anuncia, no se recorta en silencio.
export const MAX_DRAFT_BYTES = 1_000_000;

// ───────────────────────── Identidad de pestaña ─────────────────────────────
let memoryTabId = null;

function newTabId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) { /* cae al aleatorio simple */ }
  return "t" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function safeStorage(getter) {
  try { return getter(); } catch (_) { return null; }
}

export function browserSessionStorage() {
  return safeStorage(() => (typeof window !== "undefined" ? window.sessionStorage : null));
}

/** Id de esta pestaña. Estable entre recargas (sessionStorage); en memoria si el
 *  almacenamiento está bloqueado. */
export function getTabId(storage = browserSessionStorage()) {
  if (memoryTabId) return memoryTabId;
  let id = null;
  try { id = storage && storage.getItem(TAB_ID_KEY); } catch (_) { id = null; }
  if (!id) {
    id = newTabId();
    try { if (storage) storage.setItem(TAB_ID_KEY, id); } catch (_) { /* en memoria */ }
  }
  memoryTabId = id;
  return id;
}

/**
 * Reclama la identidad de la pestaña ANTES de montar nada.
 *
 * Una pestaña duplicada (o abierta con window.open) hereda una COPIA de
 * sessionStorage, id de pestaña incluido. Si la original sigue viva, las dos
 * dirían ser la misma dueña del borrador. Un candado de Web Locks por id lo
 * resuelve: si ya está cogido por otra pestaña viva, esta acuña un id nuevo y
 * arranca limpia; la original conserva el suyo y su borrador.
 *
 * Una recarga libera el candado del documento viejo al descargarlo; se reintenta
 * unos instantes para no confundir "recargué" con "me han duplicado".
 */
export async function claimTabIdentity({
  storage = browserSessionStorage(),
  locks = safeStorage(() => (typeof navigator !== "undefined" ? navigator.locks : null)),
  retries = 4, retryMs = 100,
} = {}) {
  memoryTabId = null;
  let id = getTabId(storage);
  if (!locks || typeof locks.request !== "function") return id;
  const tryHold = (candidate) => new Promise((resolve) => {
    let settled = false;
    const done = (v) => { if (!settled) { settled = true; resolve(v); } };
    try {
      locks.request(`orca-tab:${candidate}`, { ifAvailable: true }, (lock) => {
        if (!lock) { done(false); return undefined; }
        done(true);
        return new Promise(() => {});   // retenido hasta que muera el documento
      }).catch(() => done(true));
    } catch (_) { done(true); }
  });
  for (let attempt = 0; attempt <= retries; attempt++) {
    if (await tryHold(id)) return id;
    if (attempt < retries) await new Promise((r) => setTimeout(r, retryMs));
  }
  id = newTabId();
  memoryTabId = id;
  try { if (storage) storage.setItem(TAB_ID_KEY, id); } catch (_) { /* en memoria */ }
  await tryHold(id);
  return id;
}

/** Solo para tests. */
export function _resetTabIdentity() { memoryTabId = null; }

// ───────────────────────────── Saneado de URLs ──────────────────────────────
const SECRET_PARAM_RE = /(^|[_-])(sig|signature|token|key|secret|auth|credential|credentials|policy|expires|password|session|sas)([_-]|$)|^x-(amz|goog|ms)-/i;

/** URL propia del operador que se puede guardar, o null si lleva credenciales o
 *  no es http(s). */
export function safeUrl(value) {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw || raw.length > 4096) return null;
  let u;
  try { u = new URL(raw); } catch (_) { return null; }
  if (u.protocol !== "http:" && u.protocol !== "https:") return null;
  if (u.username || u.password) return null;
  for (const name of u.searchParams.keys()) {
    if (SECRET_PARAM_RE.test(name)) return null;
  }
  if (SECRET_PARAM_RE.test(u.hash.replace(/^#/, "").split("=")[0] || "")) return null;
  return raw;
}

// Free text is user data, not a media locator: preserve literal data:/blob:
// prefixes and field names. Only File/Blob values are non-persistable.
function copyFields(value) {
  return JSON.parse(JSON.stringify(value || {}, (_key, v) => isFileLike(v) ? null : v));
}

const isFileLike = (v) => (typeof File !== "undefined" && v instanceof File)
  || (typeof Blob !== "undefined" && v instanceof Blob);

// Media objects are allowlisted. Persist identity/display metadata, never an
// arbitrary nested server response (which could contain signed URLs or tokens).
const MEDIA_FIELDS = new Set([
  "id", "asset_id", "local_id", "local_asset_id", "storage_path", "thumbnail_path",
  "original_name", "original_filename", "filename", "name", "label", "role", "kind", "type", "ref_type",
  "asset_type", "byteplus_asset_type", "media_type", "media_kind", "content_type", "mime_type", "thumbnail_content_type",
  "byteplus_status", "status", "duration_seconds", "fps", "frames", "frame_count", "width", "height",
  "sample_aspect_ratio", "size", "size_bytes", "is_video", "is_audio", "contains_faces",
]);
function plain(value) {
  const out = {};
  for (const [key, item] of Object.entries(value || {})) {
    if (!MEDIA_FIELDS.has(key) || isFileLike(item)) continue;
    if (typeof item === "string" || typeof item === "boolean" || item === null
        || (typeof item === "number" && Number.isFinite(item))) out[key] = item;
  }
  return out;
}

/**
 * Referencia subida/adjunta (omni, i2v, legacy, fuente subida, frames).
 * Devuelve `null` si tras el saneado no queda NINGÚN localizador: una
 * referencia sin storage_path, asset_id ni URL propia es un fantasma, y
 * restaurarla fingiría un adjunto que no existe.
 */
export function sanitizeRef(ref) {
  if (!ref || typeof ref !== "object" || isFileLike(ref)) return null;
  const out = plain(ref) || {};
  const hasDurable = !!(out.storage_path || out.asset_id || out.local_id);
  const ownUrl = hasDurable ? null : safeUrl(ref.url) || safeUrl(ref.public_url);
  if (ownUrl) out.url = ownUrl;
  if (!hasDurable && !ownUrl) return null;
  return out;
}

/** Fila de librería (sourceAsset, frames, selectedAssets). Sin previews; el
 *  `asset_id` es el localizador y la URL se resuelve al compilar. */
export function sanitizeAsset(asset) {
  if (!asset || typeof asset !== "object" || isFileLike(asset)) return null;
  const out = plain(asset) || {};
  if (!out.asset_id && !out.id) return null;
  return out;
}

const mapKeep = (list, fn) => (Array.isArray(list) ? list.map(fn).filter(Boolean) : []);

function localFileMeta(slot, file) {
  if (!file || !isFileLike(file)) return null;
  return {
    slot, name: String(file.name || "(sin nombre)"),
    size: Number.isFinite(file.size) ? file.size : null,
    type: String(file.type || ""),
  };
}

// Valores por defecto del editor (los de `useState`). Solo sirven para decidir
// qué es "trabajo" y para validar al volver.
export const DEFAULT_SETTINGS = Object.freeze({
  activeTemplate: "background", modelId: null, taskMode: "auto", compilerMode: "raw",
  ratio: "16:9", duration: 5, durationUnit: "seconds", framesValue: 120, fps: 24,
  resolution: null, nativeDraft: false, seed: null, containsFaces: false,
  injectLabelHints: false, injectCanonicalDescriptions: true, variantCount: 1,
});

// Ajustes que el OPERADOR puede cambiar a mano. Un ajuste solo cuenta como
// "trabajo" si el operador lo tocó (`authored`) y su valor difiere del de fábrica:
// el ratio por defecto del proyecto, la duración/fps sondeados del clip o lo que
// carga un Reuse llegan solos y NO deben convertir un editor vacío en "sin enviar".
export const SETTINGS_KEYS = Object.freeze([
  "modelId", "taskMode", "compilerMode", "ratio", "duration", "durationUnit", "framesValue", "fps",
  "resolution", "nativeDraft", "seed", "containsFaces", "injectLabelHints",
  "injectCanonicalDescriptions", "variantCount",
]);
// "" y null significan lo mismo ("el default de ARK"): loadFromJob escribe "".
const normSetting = (v) => (v === "" || v === undefined ? null : v);
const authoredKeys = (list) => (Array.isArray(list) ? list : [])
  .filter((k, i, a) => SETTINGS_KEYS.includes(k) && a.indexOf(k) === i).sort();

/** Ajustes tocados por el operador cuyo valor NO es el de fábrica. */
export function authoredNonDefault(snap) {
  if (!snap) return [];
  return authoredKeys(snap.authored).filter(
    (k) => normSetting(snap[k]) !== normSetting(DEFAULT_SETTINGS[k]));
}

/**
 * Foto guardable del editor a partir de su estado vivo (`s`). Es la ÚNICA puerta
 * de entrada: lo que no se copia aquí no se persiste jamás, que es lo que da la
 * garantía de "sin consentimientos ni operaciones pendientes".
 */
export function buildDraftSnapshot(s) {
  const droppedUrls = [];
  const url = (label, value) => {
    const trimmed = typeof value === "string" ? value.trim() : "";
    if (!trimmed) return "";
    const ok = safeUrl(trimmed);
    if (!ok) droppedUrls.push(label);
    return ok || "";
  };
  const resolved = {
    video: sanitizeRef(s.uploadedVideo) || sanitizeRef(s.omniSource) || sanitizeAsset(s.sourceAsset) || safeUrl(s.videoUrl),
    image_ref: sanitizeRef(s.uploadedImage) || sanitizeAsset(s.imageRefAsset) || safeUrl(s.imgRefUrl),
    first_frame: sanitizeRef(s.uploadedFirstFrame) || sanitizeAsset(s.firstFrameAsset) || safeUrl(s.firstFrameUrl),
    last_frame: sanitizeRef(s.uploadedLastFrame) || sanitizeAsset(s.lastFrameAsset) || safeUrl(s.lastFrameUrl),
  };
  const liveFiles = [
    localFileMeta("video", s.videoFile), localFileMeta("image_ref", s.imgRefFile),
    localFileMeta("first_frame", s.firstFrameFile), localFileMeta("last_frame", s.lastFrameFile),
  ].filter((f) => f && !resolved[f.slot]);
  const localFiles = [...liveFiles, ...(s.unresolvedLocalFiles || []).filter((f) =>
    f && typeof f.name === "string" && !resolved[f.slot] && !liveFiles.some((live) => live.slot === f.slot))];
  const droppedRefs = [];
  const refs = (label, list) => {
    const src = Array.isArray(list) ? list : [];
    const kept = mapKeep(src, sanitizeRef);
    if (kept.length !== src.length) droppedRefs.push(label);
    return kept;
  };
  const snap = {
    activeTemplate: s.activeTemplate, fields: copyFields(s.fields),
    customPrompt: typeof s.customPrompt === "string" ? s.customPrompt : "",
    showPromptEdit: !!s.showPromptEdit,
    videoUrl: url("videoUrl", s.videoUrl), imgRefUrl: url("imgRefUrl", s.imgRefUrl),
    firstFrameUrl: url("firstFrameUrl", s.firstFrameUrl), lastFrameUrl: url("lastFrameUrl", s.lastFrameUrl),
    uploadedVideo: sanitizeRef(s.uploadedVideo), uploadedImage: sanitizeRef(s.uploadedImage),
    uploadedFirstFrame: sanitizeRef(s.uploadedFirstFrame), uploadedLastFrame: sanitizeRef(s.uploadedLastFrame),
    omniSource: sanitizeRef(s.omniSource),
    omniRefs: refs("omniRefs", s.omniRefs), i2vExtraRefs: refs("i2vExtraRefs", s.i2vExtraRefs),
    legacyExtraRefs: refs("legacyExtraRefs", s.legacyExtraRefs),
    selectedAssets: mapKeep(s.selectedAssets, sanitizeAsset),
    sourceAsset: sanitizeAsset(s.sourceAsset), firstFrameAsset: sanitizeAsset(s.firstFrameAsset),
    lastFrameAsset: sanitizeAsset(s.lastFrameAsset), imageRefAsset: sanitizeAsset(s.imageRefAsset),
    modelId: s.modelId ?? null, taskMode: s.taskMode, compilerMode: s.compilerMode,
    ratio: s.ratio, duration: s.duration, durationUnit: s.durationUnit,
    framesValue: s.framesValue, fps: s.fps, resolution: s.resolution ?? null,
    nativeDraft: !!s.nativeDraft, seed: s.seed ?? null, containsFaces: !!s.containsFaces,
    injectLabelHints: !!s.injectLabelHints,
    injectCanonicalDescriptions: s.injectCanonicalDescriptions !== false,
    variantCount: s.variantCount,
    // Qué ajustes tocó el operador (ver SETTINGS_KEYS): sin esto un ajuste suelto
    // no sería trabajo ni se avisaría de perderlo.
    authored: authoredKeys(s.authoredSettings ? Object.keys(s.authoredSettings) : []),
    // Jobs YA aceptados por el servidor: solo se retoma su lectura (polling).
    currentJobId: typeof s.currentJobId === "string" && s.currentJobId ? s.currentJobId : null,
    siblingJobIds: (Array.isArray(s.siblingJobIds) ? s.siblingJobIds : []).filter((x) => typeof x === "string" && x),
    localFiles,
  };
  if (s.userMarkerCount > 0 || s.unresolvedMarkerCount > 0) snap.markerCount = s.userMarkerCount || s.unresolvedMarkerCount;
  return { snapshot: snap, droppedUrls, droppedRefs };
}

const nonEmpty = (v) => (typeof v === "string" ? v.trim() !== "" : !!v);

/** ¿Hay trabajo del operador? Un ajuste cuenta SOLO si lo tocó el operador y no
 *  es el de fábrica (`authoredNonDefault`); el ratio por defecto del proyecto o
 *  la duración sondeada los cambia el sistema sin que nadie toque nada. */
export function hasWork(snap) {
  if (!snap) return false;
  if (authoredNonDefault(snap).length > 0) return true;
  if (nonEmpty(snap.customPrompt)) return true;
  if (Object.values(snap.fields || {}).some((tpl) => Object.values(tpl || {}).some(nonEmpty))) return true;
  if ([snap.videoUrl, snap.imgRefUrl, snap.firstFrameUrl, snap.lastFrameUrl].some(nonEmpty)) return true;
  if ([snap.uploadedVideo, snap.uploadedImage, snap.uploadedFirstFrame, snap.uploadedLastFrame,
    snap.omniSource, snap.sourceAsset, snap.firstFrameAsset, snap.lastFrameAsset, snap.imageRefAsset].some(Boolean)) return true;
  if ([snap.omniRefs, snap.i2vExtraRefs, snap.legacyExtraRefs, snap.selectedAssets, snap.localFiles]
    .some((l) => Array.isArray(l) && l.length > 0)) return true;
  return (snap.markerCount || 0) > 0;
}

/** Huella estable del TRABAJO (no de lo que el servidor puede refrescar): prompt,
 *  campos, localizadores y su orden, ficheros locales, marcas y los ajustes que
 *  TOCÓ el operador. Excluye ids de job, nombres/duraciones que la hidratación de
 *  librería reescribe y los ajustes no tocados (ratio por defecto del proyecto,
 *  duración sondeada…): si entraran, un Reuse recién cargado parecería "sin
 *  enviar" en cuanto llegara la primera respuesta de metadatos. */
export function snapshotSignature(snap) {
  if (!snap) return "";
  const ref = (r) => (r ? [r.storage_path || null, r.asset_id || null, r.url || null, r.label || ""] : null);
  const asset = (a) => (a ? (a.asset_id || a.id || null) : null);
  return JSON.stringify([
    snap.customPrompt, snap.fields, snap.activeTemplate,
    snap.videoUrl, snap.imgRefUrl, snap.firstFrameUrl, snap.lastFrameUrl,
    ref(snap.uploadedVideo), ref(snap.uploadedImage), ref(snap.uploadedFirstFrame),
    ref(snap.uploadedLastFrame), ref(snap.omniSource),
    (snap.omniRefs || []).map(ref), (snap.i2vExtraRefs || []).map(ref), (snap.legacyExtraRefs || []).map(ref),
    (snap.selectedAssets || []).map(asset),
    asset(snap.sourceAsset), asset(snap.firstFrameAsset), asset(snap.lastFrameAsset), asset(snap.imageRefAsset),
    (snap.localFiles || []).map((f) => [f.slot, f.name, f.size]), snap.markerCount || 0,
    authoredKeys(snap.authored).map((k) => [k, normSetting(snap[k])]),
  ]);
}

/** "Sin enviar" = hay trabajo y no coincide con lo último aceptado por el
 *  servidor (o con lo cargado desde un take). */
export function isDirty(snap, baselineSig) {
  return hasWork(snap) && snapshotSignature(snap) !== (baselineSig || null);
}

// ──────────────────────────── Validación al volver ──────────────────────────
const RATIO_RE = /^(adaptive|\d{1,2}:\d{1,2})$/;
const FPS_VALUES = [23, 24, 25, 29, 30, 50, 60];
const RESOLUTIONS = ["480p", "720p", "1080p", "4k"];

const str = (v, fallback = "") => (typeof v === "string" ? v : fallback);
const num = (v, lo, hi, fallback) => (Number.isFinite(v) && v >= lo && v <= hi ? v : fallback);
const list = (v, fn) => mapKeep(v, fn);

/**
 * Convierte un borrador leído en valores seguros para sembrar el editor.
 * `ctx` aporta lo que solo el editor sabe: plantillas válidas, brazos del
 * compilador y los validadores de modelo/modo de tarea (`modelFromJob`…).
 * Nada de lo que devuelve dispara una operación: son valores de formulario.
 */
export function restoreValues(snap, ctx) {
  const d = snap || {};
  const templates = ctx.templateIds || [];
  const out = {
    activeTemplate: templates.includes(d.activeTemplate) ? d.activeTemplate : DEFAULT_SETTINGS.activeTemplate,
    fields: d.fields && typeof d.fields === "object" && !Array.isArray(d.fields) ? d.fields : {},
    customPrompt: str(d.customPrompt), showPromptEdit: !!d.showPromptEdit || nonEmpty(d.customPrompt),
    videoUrl: str(safeUrl(d.videoUrl) || ""), imgRefUrl: str(safeUrl(d.imgRefUrl) || ""),
    firstFrameUrl: str(safeUrl(d.firstFrameUrl) || ""), lastFrameUrl: str(safeUrl(d.lastFrameUrl) || ""),
    uploadedVideo: sanitizeRef(d.uploadedVideo), uploadedImage: sanitizeRef(d.uploadedImage),
    uploadedFirstFrame: sanitizeRef(d.uploadedFirstFrame), uploadedLastFrame: sanitizeRef(d.uploadedLastFrame),
    omniSource: sanitizeRef(d.omniSource),
    omniRefs: list(d.omniRefs, sanitizeRef), i2vExtraRefs: list(d.i2vExtraRefs, sanitizeRef),
    legacyExtraRefs: list(d.legacyExtraRefs, sanitizeRef),
    selectedAssets: list(d.selectedAssets, sanitizeAsset),
    sourceAsset: sanitizeAsset(d.sourceAsset), firstFrameAsset: sanitizeAsset(d.firstFrameAsset),
    lastFrameAsset: sanitizeAsset(d.lastFrameAsset), imageRefAsset: sanitizeAsset(d.imageRefAsset),
    modelId: ctx.modelFromJob ? ctx.modelFromJob({ model_used: d.modelId }) : (d.modelId ?? null),
    taskMode: ctx.taskModeFromJob ? ctx.taskModeFromJob({ task_mode: d.taskMode }) : DEFAULT_SETTINGS.taskMode,
    compilerMode: (ctx.compilerModeIds || []).includes(d.compilerMode) ? d.compilerMode : DEFAULT_SETTINGS.compilerMode,
    ratio: RATIO_RE.test(str(d.ratio)) ? d.ratio : DEFAULT_SETTINGS.ratio,
    duration: num(d.duration, ctx.minDuration ?? 1, ctx.maxDuration ?? 60, DEFAULT_SETTINGS.duration),
    durationUnit: d.durationUnit === "frames" ? "frames" : "seconds",
    framesValue: num(d.framesValue, 1, 100000, DEFAULT_SETTINGS.framesValue),
    fps: FPS_VALUES.includes(d.fps) ? d.fps : DEFAULT_SETTINGS.fps,
    resolution: RESOLUTIONS.includes(d.resolution) ? d.resolution : null,
    nativeDraft: d.nativeDraft === true,
    seed: Number.isFinite(d.seed) ? d.seed : null,
    containsFaces: d.containsFaces === true, injectLabelHints: d.injectLabelHints === true,
    injectCanonicalDescriptions: d.injectCanonicalDescriptions !== false,
    variantCount: num(d.variantCount, 1, 3, 1),
    authored: authoredKeys(d.authored),
    currentJobId: typeof d.currentJobId === "string" && d.currentJobId ? d.currentJobId : null,
    siblingJobIds: (Array.isArray(d.siblingJobIds) ? d.siblingJobIds : []).filter((x) => typeof x === "string" && x),
    localFiles: (Array.isArray(d.localFiles) ? d.localFiles : [])
      .filter((f) => f && typeof f.name === "string").map((f) => ({
        slot: str(f.slot), name: f.name, size: Number.isFinite(f.size) ? f.size : null, type: str(f.type),
      })),
    // Las marcas dibujadas NO se restauran (llevan una preparación de servidor
    // asociada): se cuentan para avisar de que hay que volver a dibujarlas.
    markerCount: Number.isFinite(d.markerCount) && d.markerCount > 0 ? d.markerCount : 0,
  };
  return out;
}

// ─────────────────────────────── Almacenamiento ─────────────────────────────
export function scopeOf({ userId, projectId, tabId, workspace = DRAFT_WORKSPACE }) {
  if (!userId || !tabId) return null;      // sin usuario autenticado no hay borrador
  return { userId: String(userId), projectId: projectId ? String(projectId) : null, tabId: String(tabId), workspace };
}

const keyOf = (slot, sc) => `${PREFIX}:${slot}:${JSON.stringify([sc.workspace, sc.tabId, sc.userId, sc.projectId])}`;
const sameScope = (a, b) => !!a && !!b && a.userId === b.userId && a.projectId === b.projectId
  && a.tabId === b.tabId && a.workspace === b.workspace;

function failureReason(err) {
  const name = String((err && err.name) || "");
  const code = err && err.code;
  if (name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED" || code === 22 || code === 1014) return "quota";
  return "unavailable";
}

/**
 * Almacén de borradores sobre un `Storage` (sessionStorage por defecto). Todas
 * las operaciones devuelven un resultado explícito en vez de lanzar: el editor
 * tiene que poder ENSEÑAR que no se pudo guardar.
 */
export function createDraftStore({ storage = browserSessionStorage(), now = () => Date.now() } = {}) {
  let sequence = 0;
  const readRoot = (sc) => {
    if (!storage) return { status: "unavailable" };
    let raw;
    try { raw = storage.getItem(keyOf("current", sc)); } catch (_) { return { status: "unavailable" }; }
    if (raw == null) return { status: "none" };
    let record;
    try { record = JSON.parse(raw); } catch (_) { return { status: "corrupt" }; }
    if (!record || record.v !== DRAFT_VERSION || !record.owner) return { status: "corrupt" };
    if ((!record.draft && !record.journal) || (Boolean(record.draft) && typeof record.draft !== "object")) return { status: "corrupt" };
    if (record.backups && !Array.isArray(record.backups)) return { status: "corrupt" };
    if (!sameScope(record.owner, sc)) return { status: "foreign" };
    if ((record.backups || []).some((b) => !b || !b.draft || !sameScope(b.owner, sc))) return { status: "corrupt" };
    return { status: "ok", record: { ...record, backups: record.backups || [] } };
  };
  const writeRoot = (sc, record) => {
    if (!storage) return { ok: false, reason: "unavailable" };
    let text;
    try { text = JSON.stringify({ ...record, journal: true }); } catch (_) { return { ok: false, reason: "unserializable" }; }
    if (text.length > MAX_DRAFT_BYTES) return { ok: false, reason: "too_large" };
    // One atomic Storage.setItem publishes current + all history. Quota cannot
    // leave half a swap or overwrite an unrecovered backup.
    try { storage.setItem(keyOf("current", sc), text); } catch (e) { return { ok: false, reason: failureReason(e) }; }
    return { ok: true };
  };
  const editable = (sc) => {
    const root = readRoot(sc);
    if (!["ok", "none"].includes(root.status)) return { error: { ok: false, reason: root.status } };
    return { record: root.record || { v: DRAFT_VERSION, owner: sc, draft: null, backups: [], journal: true } };
  };
  const recordOf = (sc, draft, baselineSig, notes) => ({
    v: DRAFT_VERSION, owner: sc, draft, baselineSig, notes, savedAt: now(),
    id: `${now()}:${++sequence}:${Math.random().toString(36).slice(2)}`,
  });
  const addBackup = (record, backup) => {
    const list = record.backups || [];
    const identical = list.some((b) => JSON.stringify([b.draft, b.baselineSig]) === JSON.stringify([backup.draft, backup.baselineSig]));
    if (identical) return list;
    if (list.length >= 10) return null;
    return [backup, ...list];
  };
  const currentOf = (record) => {
    const { backups, journal, ...current } = record;
    return current;
  };
  return {
    available: () => !!storage,
    read(sc) { const r = readRoot(sc); return r.status === "ok" && !r.record.draft ? { status: "none" } : r; },
    readBackup(sc, id) {
      const r = readRoot(sc);
      if (r.status !== "ok") return r;
      const b = id ? r.record.backups.find((x) => x.id === id) : r.record.backups[0];
      return b ? { status: "ok", record: b } : { status: "none" };
    },
    listBackups(sc) {
      const r = readRoot(sc);
      return r.status === "ok" ? r.record.backups.map((b) => ({ id: b.id, savedAt: b.savedAt,
        label: (b.draft.customPrompt || "Borrador sin prompt").slice(0, 70) })) : [];
    },
    write(sc, draft, baselineSig = null, notes = null) {
      const e = editable(sc); if (e.error) return e.error;
      return writeRoot(sc, { ...recordOf(sc, draft, baselineSig, notes), backups: e.record.backups });
    },
    writeBackup(sc, draft, baselineSig = null, notes = null) {
      const e = editable(sc); if (e.error) return e.error;
      const backups = addBackup(e.record, recordOf(sc, draft, baselineSig, notes));
      if (!backups) return { ok: false, reason: "history_full" };
      return writeRoot(sc, { ...e.record, backups });
    },
    remove(sc) {
      const e = editable(sc);
      // Only this explicit discard API can remove a corrupt scoped record.
      if (e.error || !e.record.backups.length) {
        try { storage?.removeItem(keyOf("current", sc)); return { ok: true }; }
        catch (err) { return { ok: false, reason: failureReason(err) }; }
      }
      return writeRoot(sc, { ...e.record, draft: null });
    },
    removeBackup(sc, id) {
      const e = editable(sc); if (e.error) return e.error;
      const chosen = id || e.record.backups[0]?.id;
      return writeRoot(sc, { ...e.record, backups: e.record.backups.filter((b) => b.id !== chosen) });
    },
    demoteToBackup(sc) {
      const e = editable(sc); if (e.error) return e.error;
      if (!e.record.draft || !hasWork(e.record.draft)) return { ok: true };
      const backups = addBackup(e.record, currentOf(e.record));
      if (!backups) return { ok: false, reason: "history_full" };
      return writeRoot(sc, { ...e.record, draft: null, backups });
    },
    swapWithBackup(sc, id) {
      const e = editable(sc); if (e.error) return e.error;
      const chosen = id || e.record.backups[0]?.id;
      const target = e.record.backups.find((b) => b.id === chosen);
      if (!target) return { ok: false, reason: "missing_backup" };
      const rest = e.record.backups.filter((b) => b.id !== chosen);
      if (e.record.draft && hasWork(e.record.draft)) rest.unshift(currentOf(e.record));
      return writeRoot(sc, { ...target, backups: rest });
    },
    listOtherProjects(sc) {
      const out = [];
      if (!storage) return out;
      try {
        for (let i = 0; i < storage.length; i++) {
          const k = storage.key(i);
          if (!k || !k.startsWith(`${PREFIX}:current:`)) continue;
          let rec;
          try { rec = JSON.parse(storage.getItem(k)); } catch (_) { continue; }
          const o = rec && rec.owner;
          if (!o || o.userId !== sc.userId || o.tabId !== sc.tabId || o.workspace !== sc.workspace || o.projectId === sc.projectId) continue;
          if (rec.v === DRAFT_VERSION && hasWork(rec.draft)) out.push({ projectId: o.projectId, savedAt: rec.savedAt });
        }
      } catch (_) { /* no list; no mutation */ }
      return out;
    },
  };
}
